/**
 * buildEuropassBody — saf (pure) yardımcı fonksiyon.
 * React, router veya üçüncü-parti bağımlılığı yoktur; kolayca test edilebilir.
 *
 * Kaynak: eski konumu CabinCVWizard.jsx ~satır 300
 * Taşıma: refactor(cv) — 2026-06-29
 */

/**
 * Europass body builder — uses live cvData + local overrides + selectionState
 * Used by the 3-adım Europass wizard (Welcome → Düzenle → Kaydet).
 *
 * @param {object}        cvData             Backend'den gelen mevcut CV verisi
 * @param {object}        localEdits         Wizard içi düzenlemeler (canvas, form, unvan, hakkimda, application_info)
 * @param {Array|null}    isDeneyimleri      İş deneyimleri dizisi
 * @param {object|null}   selectionState     Bölüm/kayıt seçim durumu
 * @param {boolean}       kvkkConsent        KVKK onayı
 * @param {string|null}   fotoUrl            Yüklenen fotoğraf URL'si
 * @param {string|null}   templateId         Şablon kimliği ("classic" | "modern" | "minimal")
 * @param {Array|undefined} canvasDilDiller  Canvas üzerinden düzenlenmiş dil listesi
 * @param {Array|undefined} canvasSertifikalar Canvas üzerinden düzenlenmiş sertifika listesi
 * @returns {object} Backend'e gönderilecek body objesi
 */
export function buildEuropassBody(
  cvData,
  localEdits,
  isDeneyimleri,
  selectionState,
  kvkkConsent,
  fotoUrl,
  templateId,
  canvasDilDiller,
  canvasSertifikalar,
) {
  const base = cvData || {};

  // is_deneyimleri with per-item include_in_cv from selectionState
  let isDenArr;
  if (isDeneyimleri && isDeneyimleri.length > 0) {
    const imap = selectionState?.isDeneyimleriInclude || {};
    isDenArr = isDeneyimleri.map((e, i) => ({
      ...e,
      include_in_cv: imap[i] !== false,
    }));
  }

  // dil.diller — canvas override takes precedence when provided
  let dilObj = base.dil ? { ...base.dil } : undefined;
  const activeDillerArr = canvasDilDiller !== undefined ? canvasDilDiller : (dilObj?.diller || []);
  if (dilObj && activeDillerArr.length > 0) {
    const dmap = selectionState?.dillerInclude || {};
    dilObj = {
      ...dilObj,
      diller: activeDillerArr.map((d, i) => ({
        ...d,
        include_in_cv: dmap[i] !== false,
      })),
    };
  } else if (dilObj && canvasDilDiller !== undefined) {
    dilObj = { ...dilObj, diller: [] };
  }

  const body = {
    kvkk_consent: kvkkConsent,
    kisisel: base.kisisel,
    egitim: base.egitim,
    is_deneyimi: base.is_deneyimi,
    dil: dilObj,
    motivasyon: base.motivasyon,
  };

  // ── Mobil bölüm düzenlemeleri — kisisel_form / egitim_form / dil_form ──────
  // MobileEditSheet'ten gelen form verileri base üzerine merge edilir.
  if (localEdits?.kisisel_form) {
    const kf = localEdits.kisisel_form;
    body.kisisel = {
      ...base.kisisel,
      ad_soyad: [kf.ad, kf.soyad].filter(Boolean).join(' ') || base.kisisel?.ad_soyad,
      dogum_tarihi: kf.dogum_tarihi || base.kisisel?.dogum_tarihi,
      cinsiyet: kf.cinsiyet || base.kisisel?.cinsiyet,
      boy_cm: kf.boy_cm !== '' && kf.boy_cm != null ? Number(kf.boy_cm) : base.kisisel?.boy_cm,
      kilo_kg: kf.kilo_kg !== '' && kf.kilo_kg != null ? Number(kf.kilo_kg) : base.kisisel?.kilo_kg,
      sehir: kf.sehir || base.kisisel?.sehir,
      telefon: kf.telefon || base.kisisel?.telefon,
      eposta: kf.eposta || base.kisisel?.eposta,
      sosyal_medya: (kf.instagram || kf.linkedin)
        ? { instagram: kf.instagram || undefined, linkedin: kf.linkedin || undefined }
        : base.kisisel?.sosyal_medya,
      pasaport_var: kf.pasaport_var ?? base.kisisel?.pasaport_var,
      seyahat_engeli: kf.seyahat_engeli ?? base.kisisel?.seyahat_engeli,
      seyahat_engeli_aciklama: kf.seyahat_engeli_aciklama || base.kisisel?.seyahat_engeli_aciklama,
      adli_sicil: kf.adli_sicil ?? base.kisisel?.adli_sicil,
      adli_sicil_aciklama: kf.adli_sicil_aciklama || base.kisisel?.adli_sicil_aciklama,
      onceki_havayolu_basvurusu: kf.onceki_havayolu_basvurusu ?? base.kisisel?.onceki_havayolu_basvurusu,
      mulakate_girdi_mi: kf.mulakate_girdi_mi ?? base.kisisel?.mulakate_girdi_mi,
      elendigi_asama: kf.elendigi_asama || base.kisisel?.elendigi_asama,
    };
  }
  if (localEdits?.egitim_form) {
    const ef = localEdits.egitim_form;
    body.egitim = {
      ...base.egitim,
      mezuniyet_durumu: ef.mezuniyet_durumu || base.egitim?.mezuniyet_durumu,
      okul: ef.okul || base.egitim?.okul,
      bolum: ef.bolum || base.egitim?.bolum,
      mezuniyet_yili: ef.mezuniyet_yili !== '' && ef.mezuniyet_yili != null ? Number(ef.mezuniyet_yili) : base.egitim?.mezuniyet_yili,
      devam_eden_egitim: ef.devam_eden_egitim ?? base.egitim?.devam_eden_egitim,
      yabanci_dil_egitimi: ef.yabanci_dil_egitimi || base.egitim?.yabanci_dil_egitimi,
      // sertifikalar intentionally omitted — single source is canvasSertifikalar (see below)
      'kabin_memurluğu_egitimi': ef.kabin_memurlugu_egitimi ?? base.egitim?.['kabin_memurluğu_egitimi'],
      ilk_yardim_egitimi: ef.ilk_yardim_egitimi ?? base.egitim?.ilk_yardim_egitimi,
      diksiyon_egitimi: ef.diksiyon_egitimi ?? base.egitim?.diksiyon_egitimi,
    };
  }
  if (localEdits?.dil_form) {
    const df = localEdits.dil_form;
    body.dil = {
      ...body.dil,
      ingilizce_seviye: df.ingilizce_seviye || body.dil?.ingilizce_seviye,
      konusma_pratigi: df.konusma_pratigi || body.dil?.konusma_pratigi,
      ingilizce_mulakat_tecrubesi: df.ingilizce_mulakat_tecrubesi || body.dil?.ingilizce_mulakat_tecrubesi,
    };
  }

  if (isDenArr && isDenArr.length > 0) body.is_deneyimleri = isDenArr;
  if (fotoUrl) body.foto_url = fotoUrl;

  // inline edit overrides (unvan, hakkimda)
  const resolvedUnvan = localEdits?.unvan !== undefined ? localEdits.unvan : base.unvan;
  const resolvedHakkimda = localEdits?.hakkimda !== undefined ? localEdits.hakkimda : base.hakkimda;
  if (resolvedUnvan) body.unvan = resolvedUnvan;
  if (resolvedHakkimda) body.hakkimda = resolvedHakkimda;

  // ── application_info merge (hidden-meta dialog — lower priority than canvas) ─
  // Fills background/qualification fields not visible on the canvas.
  if (localEdits?.application_info) {
    const ai = localEdits.application_info;

    // kisisel meta fields
    const kisMeta = {};
    if (ai.cinsiyet !== undefined)                    kisMeta.cinsiyet = ai.cinsiyet;
    if (ai.boy_cm !== undefined && ai.boy_cm !== '')  kisMeta.boy_cm = Number(ai.boy_cm);
    if (ai.kilo_kg !== undefined && ai.kilo_kg !== '') kisMeta.kilo_kg = Number(ai.kilo_kg);
    if (ai.pasaport_var !== undefined)                kisMeta.pasaport_var = ai.pasaport_var;
    if (ai.seyahat_engeli !== undefined)              kisMeta.seyahat_engeli = ai.seyahat_engeli;
    if (ai.seyahat_engeli_aciklama !== undefined)     kisMeta.seyahat_engeli_aciklama = ai.seyahat_engeli_aciklama;
    if (ai.adli_sicil !== undefined)                  kisMeta.adli_sicil = ai.adli_sicil;
    if (ai.adli_sicil_aciklama !== undefined)         kisMeta.adli_sicil_aciklama = ai.adli_sicil_aciklama;
    if (ai.onceki_havayolu_basvurusu !== undefined)   kisMeta.onceki_havayolu_basvurusu = ai.onceki_havayolu_basvurusu;
    if (ai.mulakate_girdi_mi !== undefined)           kisMeta.mulakate_girdi_mi = ai.mulakate_girdi_mi;
    if (ai.elendigi_asama !== undefined)              kisMeta.elendigi_asama = ai.elendigi_asama;
    if (Object.keys(kisMeta).length > 0) {
      body.kisisel = { ...(body.kisisel || {}), ...kisMeta };
    }

    // egitim ek fields
    const egitimEk = {};
    if (ai.kabin_memurlugu_egitimi !== undefined) egitimEk['kabin_memurluğu_egitimi'] = ai.kabin_memurlugu_egitimi;
    if (ai.ilk_yardim_egitimi !== undefined)      egitimEk.ilk_yardim_egitimi = ai.ilk_yardim_egitimi;
    if (ai.diksiyon_egitimi !== undefined)        egitimEk.diksiyon_egitimi = ai.diksiyon_egitimi;
    if (Object.keys(egitimEk).length > 0) {
      body.egitim = { ...(body.egitim || {}), ...egitimEk };
    }

    // dil narrative fields
    const dilNarr = {};
    if (ai.konusma_pratigi !== undefined)              dilNarr.konusma_pratigi = ai.konusma_pratigi;
    if (ai.ingilizce_mulakat_tecrubesi !== undefined)  dilNarr.ingilizce_mulakat_tecrubesi = ai.ingilizce_mulakat_tecrubesi;
    if (Object.keys(dilNarr).length > 0) {
      body.dil = { ...(body.dil || {}), ...dilNarr };
    }

    // is_deneyimi niteleme fields
    const isDenMeta = {};
    if (ai.calistimi !== undefined)                    isDenMeta.calistimi = ai.calistimi;
    if (ai.son_is_yeri !== undefined)                  isDenMeta.son_is_yeri = ai.son_is_yeri;
    if (ai.pozisyon !== undefined)                     isDenMeta.pozisyon = ai.pozisyon;
    if (ai.sure !== undefined)                         isDenMeta.sure = ai.sure;
    if (ai.musteri_iliskileri_deneyimi !== undefined)  isDenMeta.musteri_iliskileri_deneyimi = ai.musteri_iliskileri_deneyimi;
    if (ai.satis_deneyimi !== undefined)               isDenMeta.satis_deneyimi = ai.satis_deneyimi;
    if (ai.yogun_tempo_deneyimi !== undefined)         isDenMeta.yogun_tempo_deneyimi = ai.yogun_tempo_deneyimi;
    if (ai.vardiya_deneyimi !== undefined)             isDenMeta.vardiya_deneyimi = ai.vardiya_deneyimi;
    if (ai.ekip_deneyimi !== undefined)                isDenMeta.ekip_deneyimi = ai.ekip_deneyimi;
    if (ai.zor_musteri_deneyimi !== undefined)         isDenMeta.zor_musteri_deneyimi = ai.zor_musteri_deneyimi;
    if (Object.keys(isDenMeta).length > 0) {
      body.is_deneyimi = { ...(body.is_deneyimi || {}), ...isDenMeta };
    }

    // motivasyon fields
    const motiv = {};
    if (ai.neden_kabin_memuru !== undefined)       motiv.neden_kabin_memuru = ai.neden_kabin_memuru;
    if (ai.ne_zamandir_ister !== undefined)        motiv.ne_zamandir_ister = ai.ne_zamandir_ister;
    if (ai.zorluk_farkindaligi !== undefined)      motiv['zorluk_farkindaliği'] = ai.zorluk_farkindaligi;
    if (ai.uzun_ucus_uygunlugu !== undefined)      motiv.uzun_ucus_uygunlugu = ai.uzun_ucus_uygunlugu;
    if (ai.vardiya_uygunlugu !== undefined)        motiv.vardiya_uygunlugu = ai.vardiya_uygunlugu;
    if (ai.sehir_degisimi_uygunlugu !== undefined) motiv.sehir_degisimi_uygunlugu = ai.sehir_degisimi_uygunlugu;
    if (ai.uniforma_disiplini !== undefined)       motiv.uniforma_disiplini = ai.uniforma_disiplini;
    if (ai.havayolu_beklentisi !== undefined)      motiv.havayolu_beklentisi = ai.havayolu_beklentisi;
    if (ai.bes_yil_sonra !== undefined)            motiv.bes_yil_sonra = ai.bes_yil_sonra;
    if (Object.keys(motiv).length > 0) {
      body.motivasyon = { ...(body.motivasyon || {}), ...motiv };
    }
  }

  // Canvas field-level overrides (Faz 5 canvas inline edit)
  // Applied last so they take precedence over mobile form overrides and application_info
  if (localEdits?.canvas_kisisel) {
    // Destructure instagram/linkedin for special sosyal_medya mapping
    const { instagram, linkedin, ...ckRest } = localEdits.canvas_kisisel;
    body.kisisel = { ...(body.kisisel || {}), ...ckRest };
    // Map instagram/linkedin → sosyal_medya sub-object
    if (instagram !== undefined || linkedin !== undefined) {
      const sm = { ...(body.kisisel.sosyal_medya || {}) };
      if (instagram !== undefined) sm.instagram = instagram;
      if (linkedin !== undefined) sm.linkedin = linkedin;
      body.kisisel = { ...body.kisisel, sosyal_medya: sm };
    }
  }
  if (localEdits?.canvas_egitim) {
    const ce = { ...localEdits.canvas_egitim };
    if (ce.mezuniyet_yili !== undefined && ce.mezuniyet_yili !== '') {
      ce.mezuniyet_yili = Number(ce.mezuniyet_yili) || undefined;
    }
    body.egitim = { ...(body.egitim || {}), ...ce };
  }

  // canvas_dil merge — ingilizce_seviye canvas override
  if (localEdits?.canvas_dil) {
    const cd = localEdits.canvas_dil;
    body.dil = { ...(body.dil || {}) };
    if (cd.ingilizce_seviye !== undefined) {
      body.dil = { ...body.dil, ingilizce_seviye: cd.ingilizce_seviye };
    }
  }

  // canvasSertifikalar override — list managed independently (like canvasDilDiller)
  if (Array.isArray(canvasSertifikalar)) {
    const filtered = canvasSertifikalar.filter(Boolean);
    body.egitim = { ...(body.egitim || {}), sertifikalar: filtered };
  }

  if (selectionState?.includedSections) {
    body.included_sections = selectionState.includedSections;
  }

  // Şablon seçimi — backend Literal["classic","modern","minimal"]
  body.template_id = templateId || 'classic';

  return body;
}
